import { getCachedToken, getFreshToken, setCachedToken } from './supabase';

/**
 * API client.
 *
 * All business data goes through the Worker, never straight from the browser to
 * PostgREST. The API is where authorisation, validation and business rules live;
 * RLS is a backstop, not the only gate. The SPA talks to Supabase directly for
 * exactly one thing: authentication.
 */

/** Same-origin in dev (Vite proxies /api) and in production (the Worker routes it). */
const API_BASE = '/api';

export interface ApiOk<T> {
    readonly success: true;
    readonly data: T;
}
export interface ApiErr {
    readonly success: false;
    readonly error: string;
}

export class ApiError extends Error {
    readonly status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = 'ApiError';
        this.status = status;
    }
}

/** Called when the session is definitively gone, so the app can show the login screen. */
let onUnauthenticated: (() => void) | null = null;
export function setUnauthenticatedHandler(fn: (() => void) | null): void {
    onUnauthenticated = fn;
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
    let token = getCachedToken();
    if (token === null) {
        token = await getFreshToken();
        setCachedToken(token);
    }
    if (token === null) {
        onUnauthenticated?.();
        throw new ApiError('Not signed in', 401);
    }

    const hasBody = init.body !== undefined && !(init.body instanceof FormData);
    const build = (t: string): RequestInit => ({
        ...init,
        headers: {
            ...(init.headers as Record<string, string> | undefined),
            Authorization: `Bearer ${t}`,
            ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
        },
    });

    let res = await fetch(`${API_BASE}${path}`, build(token));

    // The cached token may simply have aged out. Refresh and retry exactly once
    // before concluding the session is gone — otherwise a user gets bounced to
    // the login screen mid-task for a token that was merely stale.
    if (res.status === 401) {
        const fresh = await getFreshToken();
        if (fresh !== null && fresh !== token) {
            setCachedToken(fresh);
            res = await fetch(`${API_BASE}${path}`, build(fresh));
        }
    }

    if (res.status === 401) {
        onUnauthenticated?.();
        throw new ApiError('Your session has expired. Please sign in again.', 401);
    }

    const body = (await res.json().catch(() => null)) as ApiOk<T> | ApiErr | null;

    if (body === null) {
        throw new ApiError(`Unexpected response from ${path}`, res.status);
    }
    if (!body.success) {
        throw new ApiError(body.error, res.status);
    }
    return body.data;
}
